import {trans} from '@frontend/helpers'
import {
    useRegionConfig,
    useUserData,
} from '@frontend/hooks'
import type {
    AdDetailsData,
    AdPreviewDataForList,
} from '@frontend/modules/public/ads/AdsModuleTypes'
import {
    useAuthStore,
    useMiniChatStore,
} from '@frontend/stores'
import {
    mdiCellphoneText,
    mdiMessageReplyText,
} from '@mdi/js'
import clsx from 'clsx'
import {useState} from 'react'
import {useLocation} from 'react-router-dom'
import {Button} from 'swayok-react-mdb-ui-kit/components/Button/Button'
import {Icon} from 'swayok-react-mdb-ui-kit/components/Icon/Icon'
import {formatValueForMask} from 'swayok-react-mdb-ui-kit/components/Input/MaskedNumericInput'
import {handleErrorResponse} from 'swayok-react-mdb-ui-kit/helpers/api/ApiRequestErrorHelpers'
import {useEventCallback} from 'swayok-react-mdb-ui-kit/helpers/useEventCallback'
import type {ApiError} from 'swayok-react-mdb-ui-kit/services/ApiRequestService'
import {NavigationService} from 'swayok-react-mdb-ui-kit/services/NavigationService'
import {AnalyticsEventReporter} from '../../../../../helpers/AnalyticsEventReporter'
import {PublicAdsApi} from '../../PublicAdsApi'
import {AdDetailsSendMessageToSellerModal} from './AdDetailsSendMessageToSellerModal'

interface AdCommunicationButtonsProps<T> {
    ad: T
    className?: string
    buttonClassName?: string
    modalDepth?: number
    // Содержимое кнопок.
    // 'both' - надпись и иконка, 'text' - только надпись, 'icon' - только иконка (кнопки круглые).
    content?: 'both' | 'text' | 'icon'
    // Уменьшить размер кнопок и надписей.
    condensed?: boolean
    onAdDataChange: (ad: T) => void
    reloadAdAfterLogin?: () => Promise<boolean>
    onSendMessageButtonClick?: () => void
}

// Кнопки контакта с продавцом.
export function AdCommunicationButtons<
    T extends AdDetailsData | AdPreviewDataForList = AdDetailsData,
>(props: AdCommunicationButtonsProps<T>) {

    const {
        ad,
        className,
        modalDepth,
        content = 'both',
        condensed = false,
        onAdDataChange,
        onSendMessageButtonClick,
        buttonClassName: propsButtonClassName = content === 'icon'
            ? 'ms-3 p-3'
            : 'full-width mt-3 mt-md-4 flex-row-between',
        reloadAdAfterLogin,
    } = props

    const onlyIcons: boolean = content === 'icon'
    const noIcons: boolean = content === 'text'

    const user = useUserData()
    const isLoggedIn: boolean = !!user
    const regionConfig = useRegionConfig()
    const location = useLocation()

    const initUserAuthModal = useAuthStore(
        state => state.initUserAuthModal
    )
    const openChat = useMiniChatStore(
        state => state.openChat
    )

    // Состояние запроса данных из API.
    const [
        isProcessing,
        setIsProcessing,
    ] = useState<boolean>(false)

    // Видимость модального окна отправки сообщения продавцу.
    const [
        isMessageModalVisible,
        setIsMessageModalVisible,
    ] = useState<boolean | null>(null)

    // После успешной авторизации повторяем вызов функции.
    const afterUserLogin = useEventCallback((
        action: 'phone' | 'chat'
    ): void => {
        // Интервал нужен на получение обновленных данных объявления.
        const intervalId = setInterval(() => {
            if (isLoggedIn) {
                clearInterval(intervalId)
                switch (action) {
                    case 'phone':
                        showPhone()
                        break
                    case 'chat':
                        initMessageSending()
                        break
                }
            }
        }, 500)
        setTimeout(() => {
            clearInterval(intervalId)
        }, 10000)
    })

    // Возврат на текущую страницу после успешной регистрации.
    const afterUserRegistration = (): 'stop' => {
        NavigationService.navigateToLocation(location)
        return 'stop'
    }

    // Показать номер телефона.
    const showPhone = useEventCallback((): void => {
        if (isProcessing) {
            return
        }
        if (ad.phone) {
            // Вызываем приложение вызова на устройстве.
            window.location.assign('tel:' + ad.phone.replace(/[^0-9+]/g, ''))
            return
        }
        AnalyticsEventReporter.showAdPhone(ad.id, ad.seller.id, isLoggedIn)
        if (!isLoggedIn) {
            // Не авторизован: открываем форму авторизации.
            // После успешной авторизации повторяем вызов функции.
            initUserAuthModal(
                (): void => {
                    afterUserLogin('phone')
                    void reloadAdAfterLogin?.()
                },
                afterUserRegistration
            )
            return
        }
        setIsProcessing(true)
        PublicAdsApi.getSellerPne(ad.id)
            .then((phone: string) => {
                onAdDataChange({...ad, phone})
                if (onlyIcons) {
                    // Вызываем приложение вызова на устройстве.
                    window.location.assign('tel:' + phone.replace(/[^0-9+]/g, ''))
                }
            })
            .catch((error: ApiError) => {
                handleErrorResponse(error)
            })
            .finally(() => {
                setIsProcessing(false)
            })
    })

    // Пользователь хочет отправить сообщение продавцу.
    const initMessageSending = useEventCallback((): void => {
        if (ad.currentUser.isOwner) {
            return
        }
        onSendMessageButtonClick?.()
        if (!isLoggedIn) {
            // Не авторизован: открываем форму авторизации.
            // После успешной авторизации повторяем вызов функции.
            initUserAuthModal(
                (): void => {
                    reloadAdAfterLogin?.()
                        .then(() => {
                            afterUserLogin('chat')
                        })
                        .catch(() => {
                        })
                },
                afterUserRegistration
            )
            return
        }
        if (ad.currentUser.chatId) {
            // Чат уже существует: открываем его в мини-чате.
            openChat(ad.currentUser.chatId)
            return
        }
        setIsMessageModalVisible(true)
    })

    // Сообщение отправлено.
    const onMessageSent = useEventCallback((chatId: number) => {
        // Переходим к чату с продавцом в мини-чате.
        openChat(chatId)
        setIsMessageModalVisible(false)
        const updatedAd = {...ad}
        updatedAd.currentUser = {...ad.currentUser, chatId}
        onAdDataChange(updatedAd)
    })

    const buttonClassName = clsx(
        propsButtonClassName,
        onlyIcons
            ? 'with-icon-flex rounded-circle lh-1'
            : 'rounded-8',
        condensed ? 'button-condensed' : null
    )

    return (
        <div
            className={clsx(
                'item-communication-buttons',
                className
            )}
        >
            {ad.communicationMethods.phone && (
                <Button
                    color="green"
                    className={buttonClassName}
                    onClick={showPhone}
                    disabled={isProcessing}
                >
                    {!onlyIcons && (
                        <span>
                            {
                                ad.phone
                                    ? formatValueForMask(ad.phone, regionConfig.phone.template, false)
                                    : trans().ads.details.show_seller_phone(condensed)
                            }
                        </span>
                    )}
                    {!noIcons && (
                        <Icon
                            path={mdiCellphoneText}
                            className={onlyIcons ? '' : 'ms-3'}
                        />
                    )}
                </Button>
            )}

            {ad.communicationMethods.messages && (
                <Button
                    color="blue"
                    className={buttonClassName}
                    disabled={ad.currentUser.isOwner}
                    onClick={initMessageSending}
                    title={
                        ad.currentUser.isOwner
                            ? trans().ads.details.this_is_your_ad
                            : undefined
                    }
                >
                    {!onlyIcons && (
                        <span>
                            {
                                ad.currentUser.chatId
                                    ? trans().ads.details.open_chat_with_seller(condensed)
                                    : trans().ads.details.contact_seller_via_messages
                            }
                        </span>
                    )}
                    {!noIcons && (
                        <Icon
                            path={mdiMessageReplyText}
                            className={onlyIcons ? '' : 'ms-3'}
                        />
                    )}
                </Button>
            )}
            {isLoggedIn && !ad.currentUser.isOwner && (
                <AdDetailsSendMessageToSellerModal
                    show={isMessageModalVisible}
                    depth={modalDepth}
                    ad={ad}
                    categoryName={ad.categoryName}
                    categoryType={ad.categoryType}
                    onClose={() => {
                        setIsMessageModalVisible(false)
                    }}
                    onClosed={() => {
                        setIsMessageModalVisible(null)
                    }}
                    onMessageSent={onMessageSent}
                />
            )}
        </div>
    )
}
